/**
 * useSubCommandTabPresence — Lightweight data-presence checks for sub Command tabs.
 *
 * For subs (role === 'member'), tabs that have no content are hidden to avoid
 * empty-page confusion. Doms always see all tabs (they assign the content).
 *
 * This hook fires 5 cheap API calls in parallel, each fetching only the
 * minimum needed to determine whether content exists (limit=1 or the
 * active-lock endpoint). Results are cached with a 5-minute staleTime so
 * normal nav re-renders don't re-fire requests.
 *
 * Tabs covered:
 *   - protocols  → /protocols/my?limit=1&activeOnly=true
 *   - chastity   → /chastity/locks/active
 *   - travel     → /travel-schedules (any future schedule)
 *   - reviews    → /relationship-reviews/instances/open (any open instance)
 *   - economy    → /points/store?limit=1 (any store item configured)
 *
 * Returns { tabPresence, isLoading } where tabPresence maps each tabId to bool.
 * While loading, all tabs default to visible so there is no flash of missing tabs.
 */

import { useQuery } from '@tanstack/react-query';
import { protocolAPI, chastityAPI, travelScheduleAPI, relationshipReviewAPI, pointsAPI } from '../../services/api';
import { queryKeys } from './queryKeys';
import { unwrapResponse, unwrapArray } from '../../utils/unwrapResponse';

export interface SubCommandTabPresence {
  protocols: boolean;
  chastity: boolean;
  travel: boolean;
  reviews: boolean;
  economy: boolean;
}

interface UseSubCommandTabPresenceParams {
  /** The organization's public ID (UUID string). */
  orgId: string | undefined;
  /** Whether to actually run the queries. Pass false for doms. */
  enabled: boolean;
}

/**
 * Fetches presence data for the 5 conditional sub Command tabs.
 *
 * When `enabled` is false (dom, or flag off) all tabs report true so nothing
 * is filtered — this ensures zero behavioural change for doms.
 */
export function useSubCommandTabPresence({
  orgId,
  enabled,
}: UseSubCommandTabPresenceParams): { tabPresence: SubCommandTabPresence; isLoading: boolean } {
  const canRun = enabled && !!orgId;

  // --- Protocols: any protocol assigned to this sub ---
  const protocolsQuery = useQuery({
    queryKey: queryKeys.subCommandPresence.protocols(orgId ?? ''),
    queryFn: async () => {
      const resp = await protocolAPI.getMyProtocols({ activeOnly: true, limit: 1 });
      // pagination.total is the reliable count
      const total = (resp.data as { pagination?: { total?: number } })?.pagination?.total ?? unwrapArray(resp.data).length;
      return total > 0;
    },
    enabled: canRun,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // --- Chastity: an active lock exists for this wearer ---
  const chastityQuery = useQuery({
    queryKey: queryKeys.subCommandPresence.chastity(orgId ?? ''),
    queryFn: async () => {
      const resp = await chastityAPI.getActiveLock();
      const lock = unwrapResponse<any>(resp.data);
      return lock !== null && lock !== undefined;
    },
    enabled: canRun,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // --- Travel: any travel schedule (including future) ---
  const travelQuery = useQuery({
    queryKey: queryKeys.subCommandPresence.travel(orgId ?? ''),
    queryFn: async () => {
      const resp = await travelScheduleAPI.getAll();
      const schedules = unwrapArray(resp.data);
      return schedules.length > 0;
    },
    enabled: canRun,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // --- Reviews: any open review instance involving this sub ---
  const reviewsQuery = useQuery({
    queryKey: queryKeys.subCommandPresence.reviews(orgId ?? ''),
    queryFn: async () => {
      const resp = await relationshipReviewAPI.getOpenInstances();
      const instances = unwrapArray(resp.data);
      return instances.length > 0;
    },
    enabled: canRun,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // --- Economy/Store: any store items configured for this org ---
  const economyQuery = useQuery({
    queryKey: queryKeys.subCommandPresence.economy(orgId ?? ''),
    queryFn: async () => {
      const resp = await pointsAPI.getStoreItems({ includeInactive: false });
      const items = unwrapArray(resp.data);
      return items.length > 0;
    },
    enabled: canRun,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const isLoading = canRun && (
    protocolsQuery.isLoading ||
    chastityQuery.isLoading ||
    travelQuery.isLoading ||
    reviewsQuery.isLoading ||
    economyQuery.isLoading
  );

  // While loading or when disabled (dom), default all to true (no tabs hidden)
  const tabPresence: SubCommandTabPresence = {
    protocols: !canRun || isLoading ? true : (protocolsQuery.data ?? true),
    chastity:  !canRun || isLoading ? true : (chastityQuery.data ?? true),
    travel:    !canRun || isLoading ? true : (travelQuery.data ?? true),
    reviews:   !canRun || isLoading ? true : (reviewsQuery.data ?? true),
    economy:   !canRun || isLoading ? true : (economyQuery.data ?? true),
  };

  return { tabPresence, isLoading };
}
